"use client";

/**
 * DataMock — animated mock of the askFinz Data workspace for /apps/data.
 *
 * Three-column layout: dataset list (L) → tabular preview (C) → transform
 * pipeline (R). A small lineage strip lives at the bottom. When the mock
 * scrolls into view, a transform "applies": rows update, a brand-red
 * "12 rows changed" badge fades in, and the lineage edge between
 * pipeline steps animates.
 *
 * No data.askfinz.ai product app exists yet (just placeholder page.tsx);
 * this is presentation-only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Database,
  Table2,
  Sigma,
  Calendar,
  Type,
  Hash,
  Filter,
  GitBranch,
  Sparkles,
  ArrowUpRight,
  CheckCircle2,
  Layers,
} from "lucide-react";

type Dataset = {
  id: string;
  name: string;
  rows: string;
  updated: string;
  active?: boolean;
};

const DATASETS: Dataset[] = [
  { id: "ds-1", name: "sales_2024_q4",     rows: "84.2k",  updated: "2h ago",  active: true },
  { id: "ds-2", name: "customers_clean",   rows: "12.8k",  updated: "yesterday" },
  { id: "ds-3", name: "regions_lookup",    rows: "247",    updated: "3d ago" },
  { id: "ds-4", name: "events_raw",        rows: "1.2M",   updated: "5h ago" },
  { id: "ds-5", name: "fx_rates_daily",    rows: "5.4k",   updated: "12m ago" },
  { id: "ds-6", name: "products_catalog",  rows: "9.1k",   updated: "1d ago" },
  { id: "ds-7", name: "support_tickets",   rows: "38.5k",  updated: "1h ago" },
];

type ColType = "int" | "string" | "datetime" | "decimal";
const COL_META: Record<ColType, { icon: typeof Hash; tint: string }> = {
  int:      { icon: Hash,     tint: "text-foreground/65" },
  string:   { icon: Type,     tint: "text-foreground/65" },
  datetime: { icon: Calendar, tint: "text-foreground/65" },
  decimal:  { icon: Sigma,    tint: "text-foreground/65" },
};

const COLUMNS: { name: string; type: ColType }[] = [
  { name: "order_id",   type: "int" },
  { name: "customer",   type: "string" },
  { name: "region",     type: "string" },
  { name: "placed_at",  type: "datetime" },
  { name: "amount",     type: "decimal" },
  { name: "status",     type: "string" },
];

const ROWS_BEFORE: (string | number)[][] = [
  [10421, "A. Patel",     "EU-W",   "2024-12-02", 412.5, "paid"],
  [10422, "M. Tran",      "AS-E",   "2024-12-02", 1320.0, "paid"],
  [10423, "J. Okafor",    "AF-W",   "2024-12-02", 88.4,  "refunded"],
  [10424, "L. García",    "EU-W",   "2024-12-03", 244.0, "paid"],
  [10425, "K. Sato",      "AS-E",   "2024-12-03", null as unknown as string, "pending"],
  [10426, "R. Johansson", "EU-N",   "2024-12-03", 1990.0, "paid"],
  [10427, "P. Singh",     "AS-S",   "2024-12-04", 75.0,  "paid"],
  [10428, "C. Mendes",    "AM-S",   "2024-12-04", null as unknown as string, "pending"],
  [10429, "V. Kuznetsova","EU-E",   "2024-12-04", 308.2, "paid"],
  [10430, "D. Müller",    "EU-W",   "2024-12-05", 1102.0, "paid"],
];

// After "Drop nulls" + "Normalise dates" + "Aggregate by region" pipeline.
// In the mock, the visible change is: nulls removed (2 rows), regions rolled up.
const ROWS_AFTER: (string | number)[][] = [
  [10421, "A. Patel",      "Europe-West",  "2024-12-02", 412.5,  "paid"],
  [10422, "M. Tran",       "Asia-East",    "2024-12-02", 1320.0, "paid"],
  [10423, "J. Okafor",     "Africa-West",  "2024-12-02", 88.4,   "refunded"],
  [10424, "L. García",     "Europe-West",  "2024-12-03", 244.0,  "paid"],
  [10426, "R. Johansson",  "Europe-North", "2024-12-03", 1990.0, "paid"],
  [10427, "P. Singh",      "Asia-South",   "2024-12-04", 75.0,   "paid"],
  [10429, "V. Kuznetsova", "Europe-East",  "2024-12-04", 308.2,  "paid"],
  [10430, "D. Müller",     "Europe-West",  "2024-12-05", 1102.0, "paid"],
];

type Step = {
  id: string;
  icon: typeof Filter;
  title: string;
  detail: string;
  status: "queued" | "running" | "done";
};

const PIPELINE_INITIAL: Step[] = [
  { id: "drop-nulls", icon: Filter,     title: "Drop nulls",         detail: "amount IS NOT NULL",         status: "queued" },
  { id: "norm-dates", icon: Calendar,   title: "Normalise dates",    detail: "placed_at → ISO 8601",       status: "queued" },
  { id: "agg-region", icon: Layers,     title: "Aggregate by region",detail: "region → human-readable",    status: "queued" },
];

const LINEAGE = ["events_raw", "sales_2024", "sales_clean", "sales_2024_q4"];

export function DataMock() {
  const [rows, setRows] = useState(ROWS_BEFORE);
  const [pipeline, setPipeline] = useState<Step[]>(PIPELINE_INITIAL);
  const [showBadge, setShowBadge] = useState(false);
  const [lineageActive, setLineageActive] = useState(0);
  const [activeDataset, setActiveDataset] = useState("ds-1");
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      // Reset
      setRows(ROWS_BEFORE);
      setPipeline(PIPELINE_INITIAL);
      setShowBadge(false);
      setLineageActive(0);

      const stepRun = (idx: number, st: Step["status"]) => {
        setPipeline((cur) => cur.map((s, i) => (i === idx ? { ...s, status: st } : s)));
      };

      schedule(() => stepRun(0, "running"), 400);
      schedule(() => stepRun(0, "done"),    1100);
      schedule(() => stepRun(1, "running"), 1150);
      schedule(() => stepRun(1, "done"),    1800);
      schedule(() => stepRun(2, "running"), 1850);

      // The transform "lands" — rows update, badge fades in.
      schedule(() => {
        setRows(ROWS_AFTER);
        setShowBadge(true);
      }, 2200);

      schedule(() => stepRun(2, "done"), 2700);

      // Lineage tail-light walks across the chain.
      LINEAGE.forEach((_, i) => {
        schedule(() => setLineageActive(i + 1), 800 + i * 480);
      });

      // Loop
      schedule(runCycle, 6500);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            data.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-foreground/40" />
            Workspace
          </span>
        </div>

        <div className="grid grid-cols-12 min-h-[440px]">
          {/* Dataset list */}
          <div className="col-span-12 md:col-span-3 border-b md:border-b-0 md:border-r hairline bg-foreground/[0.02]">
            <div className="px-3 py-2 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <Database className="h-3 w-3" />
              Datasets
            </div>
            <div className="p-2 space-y-1">
              {DATASETS.map((d) => {
                const isActive = d.id === activeDataset;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setActiveDataset(d.id)}
                    className={
                      "w-full text-left rounded-md px-2.5 py-2 transition " +
                      (isActive ? "bg-foreground text-background" : "hover:bg-foreground/5")
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[12px] truncate">{d.name}</span>
                      <span className={"text-[10px] " + (isActive ? "text-background/65" : "text-foreground/65")}>
                        {d.rows}
                      </span>
                    </div>
                    <div className={"text-[10px] " + (isActive ? "text-background/55" : "text-foreground/65")}>
                      {d.updated}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview table */}
          <div className="col-span-12 md:col-span-6 flex flex-col">
            <div className="px-3 py-2 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <Table2 className="h-3 w-3" />
              <span>sales_2024_q4 · preview</span>
              {showBadge && (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[hsl(var(--accent))] text-background px-2 py-0.5 text-[9px] tracking-[0.18em] uppercase font-mono animate-[blur-fade-in_0.5s_ease-out]">
                  <CheckCircle2 className="h-2.5 w-2.5" />
                  12 rows changed
                </span>
              )}
            </div>

            <div className="flex-1 overflow-hidden font-mono text-[11px]">
              {/* Column header with type chips */}
              <div className="grid grid-cols-[repeat(6,minmax(0,1fr))] border-b hairline bg-foreground/[0.02]">
                {COLUMNS.map((c) => {
                  const M = COL_META[c.type];
                  const Icon = M.icon;
                  return (
                    <div key={c.name} className="px-2 py-2 border-r hairline last:border-r-0">
                      <div className="text-foreground/85">{c.name}</div>
                      <div className={"mt-0.5 inline-flex items-center gap-1 text-[9px] tracking-[0.16em] uppercase " + M.tint}>
                        <Icon className="h-2.5 w-2.5" />
                        {c.type}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Rows */}
              <div>
                {rows.map((r, ri) => (
                  <div
                    key={ri}
                    className="grid grid-cols-[repeat(6,minmax(0,1fr))] border-b hairline last:border-b-0 hover:bg-foreground/[0.02] animate-[blur-fade-in_0.3s_ease-out]"
                  >
                    {r.map((cell, ci) => (
                      <div key={ci} className="px-2 py-1.5 border-r hairline last:border-r-0 truncate text-foreground/80">
                        {cell === null || cell === undefined ? (
                          <span className="text-foreground/30 italic">null</span>
                        ) : (
                          String(cell)
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Transform pipeline */}
          <div className="col-span-12 md:col-span-3 border-t md:border-t-0 md:border-l hairline bg-foreground/[0.02]">
            <div className="px-3 py-2 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <Sparkles className="h-3 w-3" />
              Pipeline
            </div>
            <div className="p-3 space-y-3">
              {pipeline.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.id} className="relative">
                    <div
                      className={
                        "rounded-lg border hairline px-3 py-2.5 bg-background transition-all " +
                        (s.status === "done"
                          ? "border-foreground/30"
                          : s.status === "running"
                          ? "border-foreground/50 shadow-[0_0_0_3px_hsl(var(--foreground)/0.06)]"
                          : "")
                      }
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-foreground/65" />
                        <span className="text-[12px] text-foreground/85">{s.title}</span>
                        <span className="ml-auto">
                          {s.status === "done" ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-foreground/70" />
                          ) : s.status === "running" ? (
                            <span className="h-1.5 w-1.5 rounded-full bg-foreground animate-pulse inline-block" />
                          ) : (
                            <span className="h-1.5 w-1.5 rounded-full bg-foreground/20 inline-block" />
                          )}
                        </span>
                      </div>
                      <div className="mt-1 font-mono text-[10px] text-foreground/65">{s.detail}</div>
                    </div>
                    {i < pipeline.length - 1 && (
                      <div className="flex justify-center h-3">
                        <span className="w-px bg-foreground/15" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Lineage strip */}
        <div className="border-t hairline px-4 py-3 bg-background">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <GitBranch className="h-3 w-3" />
            Lineage
          </div>
          <div className="mt-2 flex items-center gap-2 overflow-x-auto">
            {LINEAGE.map((n, i) => (
              <div key={n} className="flex items-center gap-2">
                <div
                  className={
                    "px-2.5 py-1 rounded-md border hairline font-mono text-[11px] transition-all " +
                    (i < lineageActive
                      ? "bg-foreground text-background border-foreground"
                      : "text-foreground/65")
                  }
                >
                  {n}
                </div>
                {i < LINEAGE.length - 1 && (
                  <span
                    className={
                      "h-px w-6 transition-all duration-500 " +
                      (i + 1 < lineageActive ? "bg-foreground/60" : "bg-foreground/15")
                    }
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>
          Coming soon to <span className="font-mono text-foreground/65">data.askfinz.ai</span>
        </span>
      </div>
    </div>
  );
}
